import { authHeader, handleResponse } from "helpers";
import { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ToggleOn, ToggleOff } from "react-bootstrap-icons";
import {
  Company,
  Category,
  CostCenter,
  Department,
  Occupation,
} from "types/dataResponse";
import { BASE_URL } from "utils/requests";

const ConfigurationDashboard = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [occupations, setOccupations] = useState<Occupation[]>([]);

  useEffect(() => {
    const requestOptions = {
      method: "GET",
      headers: authHeader(),
    };

    fetch(`${BASE_URL}/company`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        setCompanies(response.data);
      });

    fetch(`${BASE_URL}/category`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        setCategories(response.data);
      });

    fetch(`${BASE_URL}/costcenter`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        setCostCenters(response.data);
      });

    fetch(`${BASE_URL}/department`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        setDepartments(response.data);
      });

    fetch(`${BASE_URL}/occupation`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        setOccupations(response.data);
      });
  }, []);

  const toggleCostCenter = (id: number) => {
    const requestOptions = {
      method: "PUT",
      headers: authHeader(),
    };

    fetch(`${BASE_URL}/costcenter/${id}/toggle-enable`, requestOptions)
      .then(handleResponse)
      .then((response) => {
        setCostCenters((prev) =>
          prev.map((cc) =>
            cc.id === id ? { ...cc, enable: response.data.enable } : cc
          )
        );
      });
  };

  return (
    <div className="container">
      <div className="row row-cols-1 row-cols-md-2">
        <div className="col mb-4">
          <div className="card shadow rounded font-weight-light">
            <div className="card-header bg-white text-secondary text-center font-weight-light d-flex justify-content-between">
              <div className="m-auto">Centro de Custo</div>
              <div>
                <a
                  href="/costcenters/new"
                  className="btn btn-outline-secondary m-0 py-0 px-2"
                  role="button"
                  aria-disabled="true"
                >
                  +
                </a>
              </div>
            </div>
            <div className="card-body bg-white">
              <div className="list-group list-group-flush custom-scrollbar-css font-weight-normal">
                {costCenters?.map((costCenter) => (
                  <div
                    className={
                      "list-group-item d-flex justify-content-between align-items-center" +
                      (costCenter.enable === false ? " text-muted bg-light" : "")
                    }
                    key={costCenter.id}
                  >
                    <a
                      className="d-flex justify-content-between align-items-center flex-grow-1 text-reset text-decoration-none mr-2"
                      href={`/costcenters/edit/${costCenter.id}`}
                    >
                      <span className="font-weight-bold">
                        {costCenter.costCenter}
                      </span>
                      <span>{costCenter.description}</span>
                    </a>
                    <OverlayTrigger
                      placement="left"
                      overlay={
                        <Tooltip id={`tooltip-costcenter-${costCenter.id}`}>
                          {costCenter.enable === false
                            ? "Habilitar"
                            : "Desabilitar"}
                        </Tooltip>
                      }
                    >
                      <button
                        type="button"
                        aria-label={
                          costCenter.enable === false
                            ? "Habilitar"
                            : "Desabilitar"
                        }
                        className={
                          "btn btn-sm ml-2 p-0 border-0 bg-transparent shadow-none d-flex align-items-center " +
                          (costCenter.enable === false
                            ? "text-danger"
                            : "text-success")
                        }
                        onClick={() => toggleCostCenter(costCenter.id)}
                      >
                        {costCenter.enable === false ? (
                          <ToggleOff size={18} />
                        ) : (
                          <ToggleOn size={18} />
                        )}
                      </button>
                    </OverlayTrigger>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col mb-4">
          <div className="card shadow rounded">
            <div className="card-header bg-white text-secondary text-center font-weight-light d-flex justify-content-between">
              <div className="m-auto">Categorias</div>
              <div>
                <a
                  href="/categories/new"
                  className="btn btn-outline-secondary m-0 py-0 px-2"
                  role="button"
                  aria-disabled="true"
                >
                  +
                </a>
              </div>
            </div>
            <div className="card-body bg-white">
              <div className="list-group list-group-flush custom-scrollbar-css font-weight-normal">
                {categories?.map((category) => (
                  <a
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    key={category.id}
                    href={`/categories/edit/${category.id}`}
                  >
                    <span className="font-weight-bold">
                      {category.codeExpense}
                    </span>
                    <span>{category.description}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col mb-4">
          <div className="card shadow rounded font-weight-light">
            <div className="card-header bg-white text-secondary text-center font-weight-light d-flex justify-content-between">
              <div className="m-auto">Empresas</div>
              <div>
                <a
                  href="/companies/new"
                  className="btn btn-outline-secondary m-0 py-0 px-2"
                  role="button"
                  aria-disabled="true"
                >
                  +
                </a>
              </div>
            </div>
            <div className="card-body bg-white">
              <div className="list-group list-group-flush custom-scrollbar-css font-weight-normal">
                {companies?.map((company) => (
                  <a
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    key={company.id}
                    href={`/companies/edit/${company.id}`}
                  >
                    <span className="font-weight-bold">{company.name}</span>
                    <span>{company.description}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col mb-4">
          <div className="card shadow rounded font-weight-light">
            <div className="card-header bg-white text-secondary text-center font-weight-light d-flex justify-content-between">
              <div className="m-auto">Setor</div>
              <div>
                <a
                  href="/departments/new"
                  className="btn btn-outline-secondary m-0 py-0 px-2"
                  role="button"
                  aria-disabled="true"
                >
                  +
                </a>
              </div>
            </div>
            <div className="card-body bg-white">
              <div className="list-group list-group-flush custom-scrollbar-css font-weight-normal">
                {departments?.map((department) => (
                  <a
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    key={department.id}
                    href={`/departments/edit/${department.id}`}
                  >
                    <span className="font-weight-bold">{department.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col mb-4">
          <div className="card shadow rounded font-weight-light">
            <div className="card-header bg-white text-secondary text-center font-weight-light d-flex justify-content-between">
              <div className="m-auto">Cargo</div>
              <div>
                <a
                  href="/occupations/new"
                  className="btn btn-outline-secondary m-0 py-0 px-2"
                  role="button"
                  aria-disabled="true"
                >
                  +
                </a>
              </div>
            </div>
            <div className="card-body bg-white">
              <div className="list-group list-group-flush custom-scrollbar-css font-weight-normal">
                {occupations?.map((occupation) => (
                  <a
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    key={occupation.id}
                    href={`/occupations/edit/${occupation.id}`}
                  >
                    <span className="font-weight-bold">{occupation.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationDashboard;
